
import { fromUInt32, fromOne, op_LeftShift, fromInt64, fromInt32, op_Multiply, op_Addition, op_Modulus } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { disposeSafe, getEnumerator, comparePrimitives, Exception } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { map, empty, iterateIndexed, ofArray, choose, sortBy, tryFind } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { startCircuitSimulationWith } from "./Simulator.js";
import { item, sortBy as sortBy_1 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { StringBuilder__AppendLine_Z721C83C5, StringBuilder_$ctor } from "../fable_modules/fable-library-js.5.13.0/System.Text.js";
import { runFastSimulation } from "./FastSim/FastRun.js";
import { extractFastSimulationOutput, changeInput } from "./FastSim/FastExtract.js";
import { convertBigintToFastData } from "./NumberHelpers.js";
import { FSInterface } from "./SimGraphTypes.js";
import { interpolate, replace, join, concat } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { toList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { toMemory } from "./RamStore.js";
import { toString } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { WaveTables } from "./SimTypes.js";

export const Constants_maxArraySize = 250;

/**
 * Deterministic per-input stimulus, stable across runs and runtimes
 */
export function stimulus(inputIndex, tick, width) {
    return op_Modulus(op_Addition(op_Multiply(fromInt32(tick + 1), fromInt64(2654435761n)), op_Multiply(fromInt32(inputIndex + 1), fromInt32(40503))), op_LeftShift(fromOne(), width));
}

function toBigint(i) {
    if (i.tag === 1) {
        throw new Exception("Algebraic value in digest simulation");
    }
    else {
        const matchValue = i.fields[0].Dat;
        if (matchValue.tag === 1) {
            return matchValue.fields[0];
        }
        else {
            return fromUInt32(matchValue.fields[0]);
        }
    }
}

/**
 * Simulate `ticks` clock cycles of `topSheet`, driving all top-level inputs with the
 * deterministic stimulus, and render the observable behaviour as text. Error when the design
 * does not build.
 * 
 * `waveTables` is a parameter only so that a test can render one design both ways and compare:
 * nothing below reads WaveComps, Drivers or WaveIndex, so the two texts must be identical, and
 * that is the whole claim the sidecar's build rests on.
 */
export function renderWith(waveTables, ldcs, topSheet, ticks) {
    const matchValue = tryFind((ldc) => (ldc.Name === topSheet), ldcs);
    if (matchValue != null) {
        let matchValue_1;
        const tupledArg = matchValue.CanvasState;
        matchValue_1 = startCircuitSimulationWith(waveTables, Constants_maxArraySize, topSheet, tupledArg[0], tupledArg[1], ldcs);
        if (matchValue_1.tag === 0) {
            const simData = matchValue_1.fields[0];
            const fs = simData.FastSim;
            const byLabel = (ios) => sortBy((tupledArg_1) => tupledArg_1[1], ios, {
                Compare: (x, y) => (comparePrimitives(x, y) | 0),
            });
            const inputs = byLabel(simData.Inputs);
            const outputs = byLabel(simData.Outputs);
            const clocked = sortBy_1((fc_1) => fc_1.FullName, fs.FClockedComps.filter((fc) => {
                if (fc.FType.tag === 40) {
                    return false;
                }
                else {
                    return true;
                }
            }), {
                Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
            });
            const viewers = sortBy((fc_3) => fc_3.FullName, choose((fc_2) => {
                if (fc_2.FType.tag === 2) {
                    return fc_2;
                }
                else {
                    return undefined;
                }
            }, ofArray(fs.FCompsByIndex)), {
                Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
            });
            const isRam = (fc_4) => {
                const matchValue_4 = fc_4.FType;
                switch (matchValue_4.tag) {
                    case 41:
                    case 42:
                        return true;
                    default:
                        return false;
                }
            };
            const sb = StringBuilder_$ctor();
            for (let tick = 0; tick <= (ticks - 1); tick++) {
                if (tick > 0) {
                    runFastSimulation(undefined, tick, fs);
                }
                iterateIndexed((i, tupledArg_2) => {
                    const width = tupledArg_2[2] | 0;
                    changeInput(tupledArg_2[0], new FSInterface(/* IData */ 0, [convertBigintToFastData(width, stimulus(i, tick, width))]), tick, fs);
                }, inputs);
                const index = (tick % fs.MaxArraySize) | 0;
                const enumerator = getEnumerator(outputs);
                try {
                    while (enumerator["System.Collections.IEnumerator.MoveNext"]()) {
                        const forLoopVar = enumerator["System.Collections.Generic.IEnumerator`1.get_Current"]();
                        StringBuilder__AppendLine_Z721C83C5(sb, `${tick},${forLoopVar[1]},${toBigint(extractFastSimulationOutput(fs, tick, forLoopVar[0], empty(), 0))}`);
                    }
                }
                finally {
                    disposeSafe(enumerator);
                }
                const enumerator_1 = getEnumerator(viewers);
                try {
                    while (enumerator_1["System.Collections.IEnumerator.MoveNext"]()) {
                        let fc_6, step, this$_1, this$_2;
                        const fc_5 = enumerator_1["System.Collections.Generic.IEnumerator`1.get_Current"]();
                        StringBuilder__AppendLine_Z721C83C5(sb, `${tick},${fc_5.FullName},${(fc_6 = fc_5, (step = (index | 0), (item(0, fc_6.Outputs).Width > 32) ? ((this$_1 = item(0, fc_6.Outputs), this$_1.BigIntSlab[(this$_1.StepBase + step)])) : fromUInt32((this$_2 = item(0, fc_6.Outputs), this$_2.UInt32Slab[(this$_2.StepBase + step)]))))}`);
                    }
                }
                finally {
                    disposeSafe(enumerator_1);
                }
                for (let idx = 0; idx <= (clocked.length - 1); idx++) {
                    let fc_8, step_3, this$_4, this$_5;
                    const fc_7 = item(idx, clocked);
                    if (!isRam(fc_7)) {
                        StringBuilder__AppendLine_Z721C83C5(sb, `${tick},${fc_7.FullName},${(fc_8 = fc_7, (step_3 = (index | 0), (item(0, fc_8.Outputs).Width > 32) ? ((this$_4 = item(0, fc_8.Outputs), this$_4.BigIntSlab[(this$_4.StepBase + step_3)])) : fromUInt32((this$_5 = item(0, fc_8.Outputs), this$_5.UInt32Slab[(this$_5.StepBase + step_3)]))))}`);
                    }
                }
            }
            for (let idx_1 = 0; idx_1 <= (clocked.length - 1); idx_1++) {
                let _arg_4, option_1, ram;
                const fc_9 = item(idx_1, clocked);
                if (isRam(fc_9)) {
                    StringBuilder__AppendLine_Z721C83C5(sb, concat("ram,", fc_9.FullName, ",", (_arg_4 = ((option_1 = fc_9.State, (option_1 != null) ? item((ticks - 1) % fs.MaxArraySize, option_1.Step) : undefined)), (_arg_4 != null) ? ((_arg_4.tag === 3) ? ((ram = _arg_4.fields[0], join(" ", map((tupledArg_3) => (`${tupledArg_3[0]}:${tupledArg_3[1]}`), toList(toMemory(ram, ticks - 1).Data))))) : "?") : "?")));
                }
            }
            return new FSharpResult$2(/* Ok */ 0, [replace(toString(sb), "\r\n", "\n")]);
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [`digest: simulation of ${topSheet} failed: ${interpolate("%A%P()", [matchValue_1.fields[0].ErrType])}`]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [concat("digest: no sheet called ", topSheet)]);
    }
}

/**
 * The digest of an ordinary build - what both runtimes compute and compare.
 */
export function render(ldcs, topSheet, ticks) {
    return renderWith(WaveTables.WithWaveTables, ldcs, topSheet, ticks);
}

